import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EMPTY, of, Subscription, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  finalize,
  mergeMap,
  switchMap,
  takeUntil,
  throttleTime,
} from 'rxjs/operators';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { environment } from 'src/environments/environment';

import { AJSTasksService } from '../../admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from '../core/alcmeon-context/alcmeon-context.service';
import { CordovaService } from '../core/cordova.service';
import { WhatsappTemplatesService } from '../parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { AbstractComponentWithScrollContainer } from '../shared/components/abstract-component-with-scroll-container';
import { SiteTypeEnum } from '../shared/enums';
import { TaskState } from '../shared/models/misc.model';
import { BasicService } from '../shared/services/basic.service';
import { ScrollService } from '../shared/services/scroll.service';
import { SuggestAnswerService } from '../shared/services/suggest-answer.service';
import { TaskService } from '../shared/services/task.service';
import { TaskListCode } from '../tasks/shared/enums';
import { AnswerEditorComponent } from './answer-editor/answer-editor.component';
import { ArchiveAndOtherActionsComponent } from './archive-and-other-actions/archive-and-other-actions.component';
import { ChunksAndIds } from './chunks-and-ids.class';
import { MessageInTaskProcessComponent } from './message-in-task-process/message-in-task-process.component';
import { PublicContextAndTaskActionsService } from './public-context-and-task-actions/public-context-and-task-actions.service';
import { ConfirmStealComponent } from './public-context-and-task-actions/task-actions-log/confirm-steal/confirm-steal.component';
import {
  AnswerAction,
  AnswersAndAfterSendAction,
  AnyTaskOrActionType,
  LazyloadTasksOrActionsResponse,
  LogAnswerRequest,
  TaskForProcessingResponse,
} from './shared/model';
import { TaskProcessContext } from './shared/task-process-context.class';
import { TaskProcessUpdatesService } from './shared/task-process-updates.service';
import { TaskProcessService } from './shared/task-process.service';

@Component({
  selector: 'app-task-process-page',
  templateUrl: './task-process-page.component.html',
  styleUrls: ['./task-process-page.component.scss'],
})
export class TaskProcessPageComponent extends AbstractComponentWithScrollContainer implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer: ElementRef<HTMLInputElement>;
  @ViewChild(ArchiveAndOtherActionsComponent) archiveAndOtherActions: ArchiveAndOtherActionsComponent;
  @ViewChild(MessageInTaskProcessComponent) firstMessageInTaskProcess: MessageInTaskProcessComponent;
  @ViewChild(AnswerEditorComponent) answerEditor: AnswerEditorComponent;

  @Input() processedTaskId?: number; // only used from angularjs (we use the route to get the task id in the native app)
  @Input() isPushboxMode = false; // only set from angularjs (the native app doesn't support push mode)
  @Output() taskLoaded = new EventEmitter<TaskProcessContext>();

  @HostBinding('style.--tpp-scrollbar-width') scrollbarWidth = '0px';

  chunks: ChunksAndIds;
  taskProcessContext: TaskProcessContext;

  isInsertingChunk = false; // yet another tentative to prevent scroll jumps when lazyloading chunks
  infiniteScrollEnabled: boolean;
  hasScrolledToProcessedTask = false; // only scroll to processed task when the page is first displayed
  // (as opposed to when appending tasks/actions from ws notifs)
  isProcesedMessageBelowViewport = false;
  isProcesedMessageAboveViewport = false;
  isUserReadOnly: boolean;

  noMoreMessages = false;
  leavingPage = false; // the page transition back to the task list looks bad without this (blurry header on top of the sliding page)
  shouldShowHeader = !environment.bundledWithAngularJSApp;
  ionContentShouldBeFullscreen = environment.isNativeApp;
  archiveButtonMessage: string;

  private subscriptions = new Subscription();
  private hasNewerMessagesSincePutOnHold; // warn of more recent messages when starting to process an on-hold message

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private taskService: TaskService,
    private taskProcessService: TaskProcessService,
    private taskProcessUpdatesService: TaskProcessUpdatesService,
    private basicService: BasicService,
    private publicContextAndTaskActionsService: PublicContextAndTaskActionsService,
    private cordovaService: CordovaService,
    private scrollService: ScrollService,
    private alcmeonContextService: AlcmeonContextService,
    private ajsTasksService: AJSTasksService,
    private suggestAnswerService: SuggestAnswerService,
    private whatsappTemplatesService: WhatsappTemplatesService
  ) {
    super();
    this.chunks = new ChunksAndIds(alcmeonContextService, this.taskProcessService);
    this.taskProcessContext = new TaskProcessContext(
      basicService,
      scrollService,
      alcmeonContextService,
      taskProcessService,
      ajsTasksService
    );
  }

  ngOnInit() {
    this.cordovaService.statusBarBlackText();
    this.updateInfiniteScrollEnabledAndNoMoreMessages();
    this.basicService.showSpinner();
    if (environment.bundledWithAngularJSApp) {
      this.initContextWhenBundledWithAngularJSApp();
    } else {
      this.initContextWhenNativeApp();
    }
    this.subscriptions.add(
      this.taskProcessContext.doScrollToTaskOrAction$
        .pipe(throttleTime(this.scrollService.scrollDurationMs()))
        .subscribe(({ taskOrActionId, shouldBlink }) => {
          this.scrollToTaskOrAction(taskOrActionId, shouldBlink);
        })
    );

    this.subscriptions.add(
      this.taskProcessService
        .getTaskForProcessing(this.taskProcessContext.processedTaskId)
        .pipe(
          finalize(() => this.basicService.hideSpinner()),
          catchError(error => this.backToTaskListIfNotCRMModeAndThrowError(error))
        )
        .subscribe(response => this.initWithTask(response))
    );
  }

  private backToTaskListIfNotCRMModeAndThrowError(error) {
    if (!this.taskProcessContext.isCRMMode) {
      this.taskProcessContext.backToTaskList();
    }
    return throwError(error);
  }

  private initContextWhenBundledWithAngularJSApp() {
    // if we are downgraded as an angularjs directive, the processed task id is an input parameter
    this.taskProcessContext.processedTaskId = this.processedTaskId;
    this.taskProcessContext.isCRMMode = false;
    this.taskProcessContext.isPushboxMode = this.isPushboxMode;
  }

  private initContextWhenNativeApp() {
    // if we are the standalone native app, the processed task id comes from the route
    this.taskProcessContext.processedTaskId = +this.route.snapshot.params.taskId;
    this.taskProcessContext.isCRMMode = this.route.snapshot.data.isCRMMode;
    const locationState = this.location.getState() as any;
    this.taskProcessContext.refererTaskListCode = locationState.taskListCode || TaskListCode.Received;
  }

  private async initWithTask(response: TaskForProcessingResponse) {
    const forceWhatsappTemplateMode = this.whatsappTemplatesService.forceWhatsAppTemplateMode();
    if (response.isWhatsappTemplateModeMandatory || forceWhatsappTemplateMode) {
      const whatsappTemplates = await this.whatsappTemplatesService
        .getAnswerTemplatesForProcessingPage(response.site.id)
        .toPromise();
      this.taskProcessContext.whatsappTemplates = whatsappTemplates;
    }

    this.taskProcessContext.initWithTask(response);
    this.isUserReadOnly = this.alcmeonContextService.isLoggedUserReadOnlyForSite(this.taskProcessContext.site.id);
    this.chunks.init(response);
    this.initHasNewerMessagesSincePutOnHold();
    this.initArchiveButtonText();
    this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunkPlusOneMessage(), null);
    this.taskLoaded.emit(this.taskProcessContext);
    this.taskProcessUpdatesService.setContext(this.taskProcessContext, this.chunks);
    this.taskProcessUpdatesService.processedMessageHasBeenDeleted$
      .pipe(takeUntil(this.taskProcessContext.ngOnDestroy$))
      .subscribe(() => (this.taskProcessContext.processedMessageHasBeenDeleted = true));

    this.initiateHandleTyping(response);
  }

  /**
   * Allows to notify a typing event to the customer by targeting its external id.
   * @remarks this mechanism only works for certain sites, see in const "sitesConfigured"
   * @param response the task that is processing.
   */
  private initiateHandleTyping(response: TaskForProcessingResponse) {
    const { id, type } = response.site;

    if (
      [SiteTypeEnum.AppleBusinessChat, SiteTypeEnum.InMessage].includes(type) &&
      !this.taskProcessContext.isReadOnlyMode
    ) {
      this.taskProcessService.handleTypingBS.next(undefined);
      this.manageTypingIndicator(id);
      if (environment.bundledWithAngularJSApp && SiteTypeEnum.AppleBusinessChat === type) {
        this.manageAppleSplitableUrl(id);
      }
    }
  }

  private manageTypingIndicator(id: number) {
    const recipientId = this.taskProcessContext.from.external_id;
    if (recipientId) {
      this.subscriptions.add(
        this.taskProcessService.handleTypingBS
          .pipe(
            takeUntil(this.taskProcessContext.ngOnDestroy$),
            filter(value => typeof value !== 'undefined'),
            switchMap(value => {
              if (value) {
                return of(value);
              }
              this.taskProcessService.notifyTypingEnd(recipientId, id).subscribe();
              return EMPTY;
            }),
            // The API allows to handle the typing event every 30 seconds but by design we want to show ASAP the 3 dots after an event type /typing-end followed by a start.
            // Use 5 seconds to be more reactive.
            throttleTime(5000),
            switchMap(() => this.taskProcessService.emitEditorHandleTyping(recipientId, id)),
            catchError(() => EMPTY)
          )
          .subscribe()
      );
      // If the user left the page, remove the event.
      this.subscriptions.add(
        this.taskProcessContext.ngOnDestroy$.subscribe(() => {
          if (this.taskProcessService.handleTypingBS.value) {
            this.taskProcessService.notifyTypingEnd(recipientId, id).subscribe();
          }
        })
      );
    }
  }

  private manageAppleSplitableUrl(id: number) {
    this.subscriptions.add(
      this.taskProcessService.handleTypingBS
        .pipe(
          takeUntil(this.taskProcessContext.ngOnDestroy$),
          filter(value => typeof value !== 'undefined'),
          debounceTime(500),
          switchMap(value => {
            this.taskProcessService.clearBZCSplittableUrlAttachements();
            const urls =
              value.match(
                /((?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim
              ) || [];
            if (urls.length) {
              this.taskProcessService.getBZCUrlData(id, urls);
            }
            return EMPTY;
          }),
          catchError(() => EMPTY)
        )
        .subscribe()
    );
  }

  private initHasNewerMessagesSincePutOnHold() {
    // If we just acquired an on-hold message, the yellow "newer messages below" button should be displayed right away
    // (as opposed to later on when we are notified of newer tasks via websocket notifs)
    this.hasNewerMessagesSincePutOnHold =
      this.taskProcessContext.processedTaskInfo?.state.previousStateCode === TaskState.OnHold &&
      this.chunks.hasTaskMoreRecentThanProcessedTask(this.taskProcessContext.processedTaskOrActionId);
  }

  private initArchiveButtonText() {
    if (this.taskProcessContext.isPublicAndProcessedMessageHasBeenDeleted()) {
      this.archiveButtonMessage = this.basicService.translate('m/task-process/message-is-deleted-archive-it-button');
    } else if (this.taskProcessContext.processedTaskInfo.isAnonymized) {
      this.archiveButtonMessage = this.basicService.translate('m/task-process/message-is-anonymized-archive-it-button');
    } else {
      this.archiveButtonMessage = this.basicService.translate('m/task-process/message-is-deleted-archive-it-button');
    }
  }

  onScrollContainerReady() {
    this.taskProcessContext.taskProcessPageWithScrollContainer = this;
    this.scrollbarWidth = `${this.scrollContainer.offsetWidth - this.scrollContainer.clientWidth}px`;
  }

  ngOnDestroy() {
    this.taskProcessContext?.destroy();
    this.taskProcessUpdatesService.clearContext(this.taskProcessContext, this.chunks);
    this.subscriptions?.unsubscribe();
  }

  ionViewDidEnter() {
    this.taskProcessContext.taskProcessPageDidEnter$.next(true);
  }

  shouldOnlyAllowArchiving() {
    return (
      this.taskProcessContext.isPublicAndProcessedMessageHasBeenDeleted() ||
      this.taskProcessContext.processedTaskInfo.isAnonymized
    );
  }

  shouldShowUnreadMessagesBelowButton() {
    // display a "new messages below" button if new messages have been received since we started processing the task,
    // or if the task's previous state was "on-hold" and there are more recent tasks in the conversation
    const newerMessagesViaWebsocket = !this.chunks.hasUserSeenAllMessages();
    return newerMessagesViaWebsocket || this.hasNewerMessagesSincePutOnHold;
  }

  onBackClicked(event) {
    this.taskProcessContext.backToTaskListRequested(event);
  }

  archiveTask() {
    this.archiveAndOtherActions.onArchiveClicked();
  }

  loadNextTasksOrActions(event) {
    // let ion-infinite-scroll know when the loading is done
    this.loadMoreTasksOrActions(this.chunks.nextTasksIds(), () => event.target.complete());
  }

  private loadMoreTasksOrActions(taskOrActionIds, onCompleteFn) {
    this.taskProcessService
      .lazyloadTasksForProcessing(taskOrActionIds)
      .pipe(
        catchError(error => {
          onCompleteFn();
          return throwError(error);
        })
      )
      .subscribe(response => this.updateChunksAndKeepScroll(response, onCompleteFn));
  }

  sendAnswer(answersAndAfterSendAction: AnswersAndAfterSendAction) {
    this.basicService.showSpinner();
    const request = this.taskProcessContext.buildLogAnswserRequest(answersAndAfterSendAction);
    const afterSendAction = answersAndAfterSendAction.afterSendAction;
    const maybeAcquire$ = this.taskProcessContext.isCRMMode
      ? this.taskService.aquireTaskFromAnyState(this.taskProcessContext.processedTaskId)
      : of(null);
    this.subscriptions.add(
      maybeAcquire$
        .pipe(mergeMap(() => this.taskProcessService.logAnswerThenExecuteAnswer(request, afterSendAction)))
        .subscribe(() => this.afterSend(request))
    );
  }

  private afterSend(request: LogAnswerRequest) {
    this.basicService.handleBZCSplittableRichLinkBS?.unsubscribe();
    if (this.taskProcessContext.isCRMMode) {
      this.answerEditor?.clearEditors();
      this.answerEditor?.focusAfterTimeout();
    } else {
      if (this.taskProcessContext.aiAssistantSuggestedAnswer) {
        const { id, suggestion, status } = this.taskProcessContext.aiAssistantSuggestedAnswer;

        this.suggestAnswerService.useAIAssistantAnswerForTaskId(this.taskProcessContext.processedTaskId, {
          suggestionId: id,
          suggestion,
          answer: request.answers[0].text,
          status,
        });
      }
      this.taskProcessContext.backToTaskList();
    }
  }

  retrySendingAnswer(answer: AnswerAction) {
    this.basicService.showSpinner();
    this.taskProcessService
      .retrySendingAnswer(answer)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(() => {
        this.basicService.toastSuccess('m/task-process/answer-sent-sucessfully');
      });
  }

  didScrollToProcessedTask() {
    this.hasScrolledToProcessedTask = true;
    this.updateInfiniteScrollEnabledAndNoMoreMessages();
    if (this.taskProcessContext.isPublic() && environment.bundledWithAngularJSApp) {
      this.publicContextAndTaskActionsService.showPublicContextAndTaskActionsLog(
        this.taskProcessContext,
        this.chunks.processedTask,
        true
      );
    }
  }

  hasPreviousOnHoldNotByBot() {
    return this.taskProcessContext.previousOnHoldNotByBotIds.length > 0;
  }

  async scrollToPreviousOnHold() {
    // a random one will do for now, as in 99% of the real world cases there will be at most one previous message on hold
    const previousOnHoldId = this.taskProcessContext.previousOnHoldNotByBotIds[0];
    await this.scrollToTaskOrAction(previousOnHoldId, true);
  }

  /**
   * scrolls to the bottom of the content, and notifies the chunks that all messages have been read
   */
  onScrollToUnreadMessagesClicked() {
    this.scrollService.scrollToBottom(this);
    this.hasNewerMessagesSincePutOnHold = false;
    setTimeout(() => this.chunks.userHasSeenAllMessages(), 2000);
  }

  dismissUnreadMessages() {
    this.chunks.userHasSeenAllMessages();
  }

  // Add classes that adjust margins for the processed message (to account for action buttons and tags),
  // and that trigger the light blue background for the processed task if it is public
  messageAndTagContainerCssClasses(taskOrActionId) {
    const classes = ['message-and-tags-container'];
    if (this.taskProcessContext.isProcessedTask(taskOrActionId)) {
      classes.push('processed-message');
    }
    if (this.taskProcessContext.isProcessedTask(taskOrActionId) && this.taskProcessContext.isPublic()) {
      classes.push('processed-message-public');
    }
    return classes;
  }

  private async scrollToTaskOrAction(taskOrActionId: string, shouldBlink: boolean) {
    await this.lazyLoadToTaskOrActionIfNotLoaded(taskOrActionId);
    // The aim is that we can just see either the day separator or the message immediately above
    // On desktop, this also makes the tooltip of <app-archive-and-other-actions> not cropped.
    const correction = 50;
    const elementId = this.taskProcessService.bubbleElementId(taskOrActionId);
    setTimeout(() => this.scrollService.scrollIonContentToElement(this, elementId, correction));
    if (shouldBlink) {
      const scrollDuration = this.scrollService.scrollDurationMs();
      setTimeout(() => this.taskProcessContext.doBlinkBubble$.next(taskOrActionId), scrollDuration);
    }
  }

  private async lazyLoadToTaskOrActionIfNotLoaded(taskOrActionId) {
    return new Promise<void>(resolve => {
      if (this.chunks.isLoaded(taskOrActionId)) {
        resolve();
      } else {
        this.loadMoreTasksOrActions(this.chunks.idsUpTo(taskOrActionId), resolve);
      }
    });
  }

  canProcessMessage() {
    return (
      !this.taskProcessContext.isConversationClosedBS.value &&
      !this.taskProcessContext.isLatestTaskTooOldForReply &&
      !this.taskProcessContext.isPublicAndProcessedMessageHasBeenDeleted() &&
      !this.taskProcessContext.processedTaskInfo.isAnonymized
    );
  }

  canStealMessage() {
    return (
      this.taskProcessContext.processedTaskInfo.state.taskStateCode === TaskState.Assigned &&
      !this.taskProcessContext.isAssignedToMe()
    );
  }

  hasAnswerEditor() {
    return !this.taskProcessContext.isReadOnlyMode && this.canProcessMessage();
  }

  hasProcessReadOnlyButton() {
    return this.taskProcessContext.isReadOnlyMode && this.canProcessMessage() && !this.canStealMessage();
  }

  hasStealButton() {
    return this.taskProcessContext.isReadOnlyMode && this.canProcessMessage() && this.canStealMessage();
  }

  processMessage() {
    if (environment.bundledWithAngularJSApp) {
      this.ajsTasksService.acquireCurrentTaskAndReloadState();
    } else {
      this.taskProcessService.reloadTaskProcessStateWithNewTask(
        this.taskProcessContext.processedTaskInfo.state.taskStateCode,
        this.taskProcessContext.processedTaskId,
        this.taskProcessContext.isCRMMode
      );
    }
  }

  stealThenProcessMessage() {
    return this.taskService
      .aquireTaskFromAnyState(this.taskProcessContext.processedTaskId)
      .subscribe(() => this.processMessage());
  }

  async confirmThenStealThenProcessMessage() {
    const modalResult = await this.basicService.popoverWithResult(null, ConfirmStealComponent, {
      adminUser: this.taskProcessContext.processedTaskInfo.state.adminUser,
    });
    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.stealThenProcessMessage();
    }
  }

  onScrollEnd() {
    const elementId = this.taskProcessService.bubbleElementId(this.taskProcessContext.processedTaskOrActionId);
    const element = this.basicService.document.getElementById(elementId);
    if (element) {
      // on mobile the ion-content seems to trigger the event before the scroll to process message
      const viewportHeight = this.scrollContainer.clientHeight;
      const topOfViewport = this.scrollContainer.scrollTop;
      const bottomOfViewPort = this.scrollContainer.scrollTop + viewportHeight;
      const topOfElement = element.offsetTop;
      const bottomOfElement = element.offsetTop + element.offsetHeight;

      // The hard-coded numbers below come from trial and error: I couldn't figure out why we need them
      this.isProcesedMessageBelowViewport = bottomOfViewPort + 50 < topOfElement;
      this.isProcesedMessageAboveViewport = topOfViewport - 125 > bottomOfElement;
    }
  }

  trackById(index, message: AnyTaskOrActionType) {
    return message?.id;
  }

  private updateInfiniteScrollEnabledAndNoMoreMessages() {
    this.infiniteScrollEnabled = this.chunks.hasMoreTasksToLoad();
    this.noMoreMessages = this.chunks.loadedChunks.length > 0 && !this.chunks.hasMoreTasksToLoad();
  }

  private updateChunksAndKeepScroll(response: LazyloadTasksOrActionsResponse, onCompleteFn) {
    const firstMessageBeforeUpdate = this.firstMessageInTaskProcess.elRef;
    this.isInsertingChunk = true;
    this.chunks.updateWithOlderMessages(response);
    this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunk(), null);

    // first timeout to prevent the browser from trying to manage the scroll itself,
    // and to give it a chance to render the new messages in the new, 'position: absolute' chunk
    setTimeout(() => {
      // We want to measure how much the first message moves away from the content-container as we add the new chunk,
      // and substract that from the scroll so that it stays at the same place in the viewport
      // (we can't use the new chunk size, because the day indicator at the top of the current chunk might disappear)
      const offsetBeforeUpdate = this.distanceBetweenElementRefs(this.messagesContainer, firstMessageBeforeUpdate);

      // update previous last message's day indicator
      this.taskProcessService.setUIProperties(this.chunks.mostRecentlyLoadedChunkPlusOneMessage(), null);

      // the 'no older message' div, if present, will also change the chunk's offset; let's update it now
      this.updateInfiniteScrollEnabledAndNoMoreMessages();

      // this will remove 'position: absolute' from the first chunk
      this.isInsertingChunk = false;

      // let ionic know that lazyloading has complete (this will hide the spinner)
      onCompleteFn();

      // another timeout to give angular a chance to update the DOM
      setTimeout(() => {
        const offsetAfterUpdate = this.distanceBetweenElementRefs(this.messagesContainer, firstMessageBeforeUpdate);
        this.scrollService.adjustScrollForPage(this, offsetAfterUpdate - offsetBeforeUpdate);
      }, 0);
    }, 250);
  }

  private distanceBetweenElementRefs(elementRef1, elementRef2) {
    return elementRef2.nativeElement.offsetTop - elementRef1.nativeElement.offsetTop;
  }
}
